import { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { Claim, ClaimType, Product, ReconcileReport, SkuVersion, SkuVersionStatus, skuVersionStatusLabels, SyncReport } from "../api/models"
import { optionalPositiveInt } from "./AddSkuDialog"
import { InstallerFieldsView, requiredMark, useInstallerFields } from "./InstallerFields"
import ProductFilter from "./widgets/ProductFilter"
import Spinner from "./widgets/Spinner"
import { trashIcon } from "./widgets/pillIcons"
import { RootState } from "../redux/store"
import { backdropDismiss } from "../utils/backdropDismiss"

interface LicenseRow {
  key: number
  product: Product | null
  maxActivationsText: string
  maxYearlyText: string
  retroactive: boolean
}

interface TopupRow {
  key: number
  product: Product | null
  amountText: string
  expiryDays: number | null      // carried through unchanged (no UI to edit it)
}

// true when a > b, both "MAJOR.MINOR.PATCH" — versions are increment-only
// (the entitled-set math orders history by number)
function versionGreaterThan(a: string, b: string): boolean {
  const pa = a.split(".").map(Number)
  const pb = b.split(".").map(Number)
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i]) return pa[i] > pb[i]
  }
  return false
}

// The intent chooser's suggestions off the latest release: a hotfix bumps
// PATCH (staying below a minor-bumped feature draft, so it can ship to the
// released line while the draft is in progress), features bump MINOR with
// the patch reset (leaving the patch lane free for future hotfixes).
type VersionIntent = "hotfix" | "features"

function suggestNextVersion(latest: string, intent: VersionIntent): string {
  const [major, minor, patch] = latest.split(".").map(Number)
  return intent === "hotfix" ? `${major}.${minor}.${patch + 1}` : `${major}.${minor + 1}.0`
}

// One line of the summary page's change list.
interface ClaimChange {
  kind: "added" | "removed" | "changed"
  name: string
  details: string[]
}

const changeMarkers: Record<ClaimChange["kind"], { marker: string, color: string }> = {
  added: { marker: "+", color: "var(--nfa-blue)" },
  removed: { marker: "−", color: "var(--nfa-orange)" },
  changed: { marker: "~", color: "var(--nfa-yellow)" },
}

function ChangeLine({ change }: { change: ClaimChange }) {
  const { marker, color } = changeMarkers[change.kind]
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
      <span style={{ color, fontWeight: 700, width: 12, flexShrink: 0, textAlign: "center" }}>{marker}</span>
      <span>
        <strong>{change.name}</strong>
        {change.details.length > 0 && (
          <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}> · {change.details.join(" · ")}</span>
        )}
      </span>
    </div>
  )
}

// shared grid template so the editor columns align across rows (and with
// the header line); the fixed widths match the inputs they hold. BOTH
// products sections share one template so their columns line up with each
// other.
const licenseGridTemplate = "200px 90px 90px 24px"

// justifyContent centers the fixed track block inside the full-width row, so
// the table sits centered in the dialog while every row (and both sections)
// keeps identical column positions
const gridRowStyle = (template: string): React.CSSProperties => ({
  display: "grid", gridTemplateColumns: template, gap: 8, alignItems: "center", justifyContent: "center", textAlign: "left"
})
const gridHeaderStyle = (template: string): React.CSSProperties => ({
  ...gridRowStyle(template), color: "var(--nfa-light-grey)", fontSize: "9pt", textAlign: "center"
})

const wizardPageTitles = {
  version: "Version",
  products: "Products",
  release: "Lifecycle & installers",
  summary: "Summary",
} as const
type WizardPage = keyof typeof wizardPageTitles

// Add a version of an existing sku (as a DRAFT by default, or released
// directly) — or, with `editVersion`, edit an existing draft/beta version in
// place (released versions are immutable; the installers physically contain
// a release's products, so the set and the release stay atomic). A
// four-page wizard: 1. the version — the hotfix/features intent chooser and
// the number it suggests (the two belong together), 2. the plug-in set
// (seeded from the current claims, edited in place), 3. the draft/release
// choice + installers (after products, so the installer requirement is
// known), 4. a summary diffing the set against the current claims and
// double-confirming a release. A HOTFIX skips the products page entirely
// and sends no claims — the server copies the latest release's exact set. Releases reconcile right after; draft/beta
// edits re-sync the version's holders — the dialog ends on a done-step with
// the report either way.
//
// RETROACTIVITY is a release-moment decision, never a per-row checkbox:
// when "Release now" is chosen and the set adds plug-ins over the previous
// release, the summary asks which of them go to owners of older versions
// (the promote dialog asks the same for the draft/beta→released path).
// Drafts store retroactive=false for new plug-ins; continuing plug-ins carry
// their seeded flags forward invisibly, like topups.
//
// Credits (topup) claims are NOT edited here: any existing ones ride along
// verbatim (the claims body is the exact set, so dropping them would strip
// them). The plan is to detach topups from sku versions entirely — they'll
// live on offerings/code batches instead, and a version becomes a pure
// product set — so no new UI invests in version-bound credits.
// Conditionally mounted (OfferingDialog pattern).
export function AddVersionDialog({ skuId, skuOwnerId, latestVersion, allVersionNumbers, currentClaims, everHadProducts, editVersion, wipVersion, productEntities, isAdmin, accessToken, onClose, onSaved }: {
  skuId: string
  // the latest RELEASED "MAJOR.MINOR.PATCH", or null while nothing is
  // released; every version (drafts included) must sort above it —
  // client-side hint, the server 400 remains the truth
  latestVersion: string | null
  // every existing version number (drafts/betas included) with its row id —
  // the client-side collision check
  allVersionNumbers: { id: string, version: string }[]
  // the seed for the editable set: the latest released version's claims (or
  // the newest row's while nothing is released); in edit mode, the edited
  // version's own claims
  currentClaims: Claim[]
  // a RELEASED version of the sku carries a license claim — once it has
  // shipped products, every release keeps requiring installers
  // (server-enforced too)
  everHadProducts: boolean
  // present = edit this draft/beta version in place (PATCH) instead of
  // creating a new one
  editVersion?: SkuVersion
  // add mode: the sku's existing unreleased version, if any. At most one may
  // exist (server-enforced), so while it does, the lifecycle choice is
  // hidden and the dialog is forced to "Release now" (the hotfix path)
  wipVersion?: SkuVersion
  // resolves the seeded claims' Product objects (cached by the Content
  // card's fetch)
  productEntities: RootState["products"]["entities"]
  // the sku's owner — the product search's default scope: a version bundles
  // the owner's products unless an admin explicitly widens the search
  skuOwnerId: string
  isAdmin: boolean
  accessToken: string
  onClose: () => void
  onSaved: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const editing = editVersion !== undefined
  // while a WIP exists (and something is released), Add IS the hotfix lane —
  // the server rejects a second draft and allows direct releases precisely so
  // hotfixes don't wait for a long-running beta. The intent chooser is hidden
  // then (its "features" suggestion would usually collide with the WIP's own
  // number); the version field stays editable as the escape hatch for a
  // deliberate direct feature release below the WIP.
  const hotfixOnly = !editing && wipVersion !== undefined && latestVersion !== null
  // the versioning intent (add mode, once something is released): fixed to
  // hotfix while a WIP exists, features otherwise, so fresh drafts bump
  // MINOR and leave the patch lane free
  const defaultIntent: VersionIntent = wipVersion !== undefined ? "hotfix" : "features"
  const [intent, setIntent] = useState<VersionIntent>(defaultIntent)
  const installer = useInstallerFields(accessToken, editing ? {
    version: editVersion.version,
    macUrl: editVersion.downloadMacUrl ?? "",
    winUrl: editVersion.downloadWinUrl ?? ""
  } : latestVersion !== null ? {
    version: suggestNextVersion(latestVersion, defaultIntent)
  } : undefined)
  // toggle = re-suggest (overwrites manual edits, predictably)
  const chooseIntent = (next: VersionIntent) => {
    setIntent(next)
    if (latestVersion !== null)
      installer.setVersion(suggestNextVersion(latestVersion, next))
  }
  // a hotfix ships the released line's EXACT set — the wizard skips the
  // products page and the request omits `claims`, so the server copies the
  // latest release's set verbatim (also immune to seed cache misses).
  // Changing the set is what a feature update is for.
  const hotfixIntent = !editing && latestVersion !== null && intent === "hotfix"
  // add mode: save as draft (default) or release in one step — forced to
  // release-now while another version is still draft/beta (one
  // work-in-progress at a time; server-enforced too). A HOTFIX always
  // releases directly regardless: its meaning is "ship the released line's
  // exact set now", and a hotfix draft would squat the single WIP slot and
  // block feature work — a patch that deserves staging goes through the
  // feature flow with a hand-typed number instead.
  const draftBlocked = wipVersion !== undefined
  const [releaseNowChoice, setReleaseNowChoice] = useState(draftBlocked)
  const releaseNow = hotfixIntent || releaseNowChoice
  // admin only: widen the product search from the sku owner's products to
  // every owner's (developers are server-scoped to their own regardless)
  const [allOwnersProducts, setAllOwnersProducts] = useState(false)
  // summary page, "Release now" only: which of the plug-ins NEW versus the
  // previous release go to owners of older versions (retroactive) — the
  // release moment's decision, never a per-row checkbox
  const [retroIds, setRetroIds] = useState<Set<string>>(new Set())
  const nextKey = useRef(0)
  // seeded once on mount from the current latest's claims; rows whose
  // product is not in the cache are dropped (shouldn't happen in practice —
  // the Content card loaded them) and counted for the warning below
  const [seedSkipped] = useState(() =>
    currentClaims.filter(c => productEntities[c.productId]?.data == null).length)
  const [licenseRows, setLicenseRows] = useState<LicenseRow[]>(() =>
    currentClaims
      .filter(c => c.type === ClaimType.License && productEntities[c.productId]?.data != null)
      .map(c => ({
        key: nextKey.current++,
        product: productEntities[c.productId]!.data!,
        maxActivationsText: c.maxActivations != null ? String(c.maxActivations) : "2",
        maxYearlyText: c.maxYearlyActivations != null ? String(c.maxYearlyActivations) : "",
        retroactive: c.retroactive
      })))
  // credits (topup) claims ride along VERBATIM — no UI edits them (see the
  // header comment); dropping them from the exact-set claims body would
  // strip them, so they're seeded and re-sent untouched
  const [topupRows] = useState<TopupRow[]>(() =>
    currentClaims
      .filter(c => c.type === ClaimType.Topup && productEntities[c.productId]?.data != null)
      .map(c => ({
        key: nextKey.current++,
        product: productEntities[c.productId]!.data!,
        amountText: c.amount != null ? String(c.amount) : "",
        expiryDays: c.expiryDays ?? null
      })))
  const [page, setPage] = useState<WizardPage>("version")
  // the summary's two-click confirm: first click arms, second releases
  const [armed, setArmed] = useState(false)
  const [step, setStep] = useState<"form" | "saving" | "done" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")
  const [report, setReport] = useState<ReconcileReport | null>(null)
  const [syncReport, setSyncReport] = useState<SyncReport | null>(null)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const addLicenseRow = () => setLicenseRows(rows => [...rows, {
    key: nextKey.current++, product: null,
    maxActivationsText: "2", maxYearlyText: "", retroactive: false
  }])
  const patchLicenseRow = (key: number, patch: Partial<LicenseRow>) =>
    setLicenseRows(rows => rows.map(r => r.key === key ? { ...r, ...patch } : r))

  const busy = step === "saving"
  // while any row holds another owner's product, the search MUST stay
  // widened — the checkbox is forced on and locked, and falls back to the
  // manual choice once the last foreign row is removed
  const hasForeignProducts = licenseRows.some(r => r.product !== null && r.product.ownerId !== skuOwnerId)
  const includeAllOwners = allOwnersProducts || hasForeignProducts
  const version = installer.version.trim()
  const versionParses = /^\d+\.\d+\.\d+$/.test(version)
  // every version — drafts included — must sort above the latest released
  // (released history is monotonic), and must not collide with any existing
  // row (the edited version's own number is fine)
  const notIncrementing = versionParses && latestVersion !== null && !versionGreaterThan(version, latestVersion)
  const collisionRow = versionParses ? allVersionNumbers.find(v => v.version === version && v.id !== editVersion?.id) : undefined
  const versionError = notIncrementing
    ? `Version must be greater than the latest released (v${latestVersion}).`
    : collisionRow !== undefined
      ? collisionRow.id === wipVersion?.id
        ? `Version ${version} is taken by your ${wipVersion.status} — renumber it (Edit) to free the number.`
        : `Version ${version} already exists.`
      : null
  // non-blocking: a hotfix numbered at/above the WIP is legal, but the WIP
  // will then have to renumber before its own release
  const versionWarning = !editing && wipVersion !== undefined && versionParses && versionError === null
    && !versionGreaterThan(wipVersion.version, version)
    ? `Above your ${wipVersion.status} v${wipVersion.version} — it will need renumbering before it can be released.`
    : null
  const macProvided = installer.macFile !== null || installer.macUrl.trim() !== ""
  const winProvided = installer.winFile !== null || installer.winUrl.trim() !== ""
  const uniqueProducts = (rows: { product: Product | null }[]) =>
    new Set(rows.map(r => r.product?.id)).size === rows.length
  const licenseRowValid = (r: LicenseRow) =>
    r.product !== null
    && Number.isInteger(Number(r.maxActivationsText)) && Number(r.maxActivationsText) > 0
    && optionalPositiveInt(r.maxYearlyText)

  // installers (both platforms, file or URL each) are required for a RELEASE
  // once the sku ships products — this release's license claims or any
  // released version's; a credits-only sku releases installer-less. Drafts
  // stage without installers; an alpha/beta with license claims must keep
  // them (the team and testers hold real downloads). Voluntary installers
  // still come in pairs.
  const installersNeeded = editing
    ? (editVersion.status === SkuVersionStatus.Alpha || editVersion.status === SkuVersionStatus.Beta) && licenseRows.length > 0
    : releaseNow && (licenseRows.length > 0 || everHadProducts)
  const releaseValid =
    installer.valid && versionParses && versionError === null
    && (installersNeeded ? macProvided && winProvided : macProvided === winProvided)
  const productsValid = licenseRows.every(licenseRowValid) && uniqueProducts(licenseRows)
  const versionPageValid = versionParses && versionError === null
  const pageValid: Record<WizardPage, boolean> = {
    version: versionPageValid,
    products: productsValid,
    release: releaseValid,
    // a hotfix sends no claims, so the (skipped) products page can't gate it
    summary: releaseValid && (hotfixIntent || productsValid),
  }

  // version first (the intent decides the number), then content, then the
  // lifecycle/installers page whose requirements depend on the chosen claims;
  // a hotfix has no content to edit, so its wizard skips the products page
  const pages: WizardPage[] = hotfixIntent
    ? ["version", "release", "summary"]
    : ["version", "products", "release", "summary"]
  const pageIndex = pages.indexOf(page)
  const goTo = (target: WizardPage) => {
    setArmed(false)
    setPage(target)
  }

  /* Summary: diff the final set against the current latest's claims. */

  const prevLicenseByProduct = new Map(currentClaims.filter(c => c.type === ClaimType.License).map(c => [c.productId, c]))

  // section split for the products page: a row is "new" while its product
  // wasn't in the previous version (or is still unselected)
  const isNewLicenseRow = (row: LicenseRow) => row.product === null || !prevLicenseByProduct.has(row.product.id)
  const preExistingLicenseRows = licenseRows.filter(r => !isNewLicenseRow(r))
  const newLicenseRows = licenseRows.filter(isNewLicenseRow)

  // whether the summary's retroactivity decision applies: releasing now over
  // an existing release, with plug-ins the previous release didn't carry
  const retroDecisionRows = !editing && releaseNow && latestVersion !== null
    ? newLicenseRows.filter(r => r.product !== null)
    : []

  // one row of either products section
  const renderLicenseRow = (row: LicenseRow) => (
    <div key={row.key} style={gridRowStyle(licenseGridTemplate)}>
      <div className="claims-editor-product">
        <ProductFilter
          selected={row.product}
          // selecting a PRE-EXISTING product restores the previous version's
          // whole claim — params AND the (hidden-checkbox) retroactive flag —
          // so removing and re-adding a product is a true no-op instead of
          // silently resetting it to the defaults
          onSelect={(product) => {
            const prev = product !== null ? prevLicenseByProduct.get(product.id) : undefined
            patchLicenseRow(row.key, prev !== undefined ? {
              product,
              maxActivationsText: prev.maxActivations != null ? String(prev.maxActivations) : "2",
              maxYearlyText: prev.maxYearlyActivations != null ? String(prev.maxYearlyActivations) : "",
              retroactive: prev.retroactive
            } : { product })
          }}
          accessToken={accessToken}
          clearable={false}
          // default scope: the sku owner's products (an admin widens via the
          // checkbox below the sections; developers are server-scoped anyway)
          ownerId={includeAllOwners ? undefined : skuOwnerId}
          // flag another owner's product right on its chip
          warning={row.product !== null && row.product.ownerId !== skuOwnerId
            ? `Owned by ${row.product.ownerId} — not the SKU owner` : undefined}
          // a product can appear only once across the sections
          disabledIds={licenseRows.filter(r => r.key !== row.key && r.product !== null).map(r => r.product!.id)}
          disabledHint="This product is already in the version"
        />
      </div>
      <input
        type="number" min={1} step={1}
        value={row.maxActivationsText}
        onChange={(e) => patchLicenseRow(row.key, { maxActivationsText: e.target.value })}
        className="generate-codes-input"
        disabled={busy}
        title="Max activations"
      />
      <input
        type="number" min={1} step={1}
        value={row.maxYearlyText}
        onChange={(e) => patchLicenseRow(row.key, { maxYearlyText: e.target.value })}
        className="generate-codes-input"
        disabled={busy}
        title="Max activations per year (empty = unlimited)"
        placeholder="unlimited"
      />
      <button
        className="card-link-danger"
        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#e0433c" }}
        onClick={() => setLicenseRows(rows => rows.filter(r => r.key !== row.key))}
        disabled={busy}
        aria-label="Remove product"
      >
        {trashIcon}
      </button>
    </div>
  )
  const productName = (productId: string) => productEntities[productId]?.data?.name ?? productId

  const licenseChanges: ClaimChange[] = []
  for (const row of licenseRows) {
    if (row.product === null) continue
    const prev = prevLicenseByProduct.get(row.product.id)
    if (prev === undefined) {
      const details = [`${row.maxActivationsText} seats`]
      if (row.maxYearlyText !== "") details.push(`${row.maxYearlyText}/year`)
      licenseChanges.push({ kind: "added", name: row.product.name, details })
      continue
    }
    const details: string[] = []
    if (String(prev.maxActivations ?? "") !== row.maxActivationsText)
      details.push(`seats ${prev.maxActivations ?? "?"} → ${row.maxActivationsText}`)
    if ((prev.maxYearlyActivations != null ? String(prev.maxYearlyActivations) : "") !== row.maxYearlyText)
      details.push(`per-year ${prev.maxYearlyActivations ?? "unlimited"} → ${row.maxYearlyText === "" ? "unlimited" : row.maxYearlyText}`)
    if (details.length > 0)
      licenseChanges.push({ kind: "changed", name: row.product.name, details })
  }
  const finalLicenseProductIds = new Set(licenseRows.map(r => r.product?.id))
  for (const prev of Array.from(prevLicenseByProduct.values()))
    if (!finalLicenseProductIds.has(prev.productId))
      licenseChanges.push({ kind: "removed", name: productName(prev.productId), details: ["current owners keep their licenses"] })
  const unchangedLicenseCount = licenseRows.filter(r =>
    r.product !== null && prevLicenseByProduct.has(r.product.id)
    && !licenseChanges.some(c => c.kind === "changed" && c.name === r.product!.name)).length

  // the retro warning follows the summary's checkboxes (release-now only)
  const retroDepositNames = retroDecisionRows.filter(r => retroIds.has(r.product!.id)).map(r => r.product!.name)
  const hasChanges = licenseChanges.length > 0

  const handleSubmit = async () => {
    if (!pageValid.summary) return
    setStep("saving")
    setErrorMessage("")
    try {
      const urls = await installer.upload(skuId)
      const claims = [
        ...licenseRows.map(r => ({
          type: "license" as const,
          productId: r.product!.id,
          maxActivations: Number(r.maxActivationsText),
          maxYearlyActivations: r.maxYearlyText === "" ? undefined : Number(r.maxYearlyText),
          // continuing plug-ins carry their seeded flag forward; new ones
          // follow the summary's release-moment decision (drafts and edit
          // mode store false — the promote dialog decides at release)
          retroactive: prevLicenseByProduct.has(r.product!.id)
            ? r.retroactive
            : !editing && releaseNow && retroIds.has(r.product!.id)
        })),
        ...topupRows.map(r => ({
          type: "topup" as const,
          productId: r.product!.id,
          amount: Number(r.amountText),
          expiryDays: r.expiryDays ?? undefined
        }))
      ]
      if (editing) {
        const { sync } = await Api.patchSkuVersion({
          skuId,
          versionId: editVersion.id,
          // urls sent explicitly (possibly unchanged — the fields were
          // seeded with the current ones); null clears
          patch: {
            version,
            downloadMacUrl: urls.downloadMacUrl ?? null,
            downloadWinUrl: urls.downloadWinUrl ?? null,
            claims
          },
          accessToken
        })
        setSyncReport(sync)
        setReport(sync?.reconcile ?? null)
      } else {
        const { reconcile } = await Api.createSkuVersion({
          skuId,
          // both urls present whenever installersNeeded (release page gate);
          // a draft or credits-only release legitimately sends neither
          version: {
            version,
            status: releaseNow ? SkuVersionStatus.Released : SkuVersionStatus.Draft,
            downloadMacUrl: urls.downloadMacUrl, downloadWinUrl: urls.downloadWinUrl,
            // hotfix: omit claims — the server copies the latest release's
            // exact set (the seeded rows may have dropped cache misses)
            claims: hotfixIntent ? undefined : claims
          },
          accessToken
        })
        setReport(reconcile)
      }
      onSaved()
      setStep("done")
    } catch (err) {
      setArmed(false)
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      style={{ maxWidth: 780 }}
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content no-scrollbar" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">{editing ? `Edit Version v${editVersion.version}` : hotfixOnly ? "Add Hotfix" : "Add Version"}</h2>

        {step === "done" ? (
          <>
            {editing ? (
              <p>
                Version {version} updated.
                {syncReport !== null && (syncReport.revokedCount > 0 || (report?.depositedCount ?? 0) > 0)
                  ? ` Holder licenses re-synced: ${report?.depositedCount ?? 0} deposited, ${syncReport.revokedCount} revoked.`
                  : ""}
              </p>
            ) : report !== null ? (
              <p>
                Version {version} released. Deposited {report.depositedCount} license{report.depositedCount === 1 ? "" : "s"} across{" "}
                {report.entitlementsScanned} entitlement{report.entitlementsScanned === 1 ? "" : "s"}
                {report.skippedCount > 0 ? ` (${report.skippedCount} skipped)` : ""}.
              </p>
            ) : (
              <p>
                Draft v{version} saved, and a dev license was deposited to
                your account so you can implement and test licensing right
                away. Promote it to alpha (your team gets licenses to test
                it), to beta, or release it from the Versions card — the
                dev license is replaced when the version moves on.
              </p>
            )}
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Close</button>
            </div>
          </>
        ) : (
          <>
            <p className="generate-codes-subtitle">
              Step {pageIndex + 1} of {pages.length} — {page === "release" && (editing || draftBlocked) ? "Installers" : wizardPageTitles[page]}
            </p>

            {page === "release" && (
              <div className="generate-codes-form">
                {/* the draft/release choice — add mode only (an edit keeps
                    the version's status; promotion is its own action), and
                    only while it IS a choice: a WIP forces release-now, a
                    hotfix always releases directly, and a radio group with
                    one disabled option is noise (the hotfix-only dialog
                    already explains the constraint on its version page) */}
                {!editing && draftBlocked && !hotfixOnly && (
                  <p className="generate-codes-hint" style={{ textAlign: "left" }}>
                    v{wipVersion!.version} is still {wipVersion!.status} — one
                    version in progress at a time, so this version releases
                    directly.
                  </p>
                )}
                {!editing && !draftBlocked && hotfixIntent && (
                  <p className="generate-codes-hint" style={{ textAlign: "left" }}>
                    Hotfixes release directly to the current line. To stage a
                    patch for testing instead, go back and pick Feature
                    update, then type the patch number yourself.
                  </p>
                )}
                {!editing && !draftBlocked && !hotfixIntent && (
                  <div className="generate-codes-field">
                    <span className="generate-codes-label">Lifecycle</span>
                    <div className="lifecycle-options">
                      <label className={`lifecycle-option${!releaseNow ? " lifecycle-option-selected" : ""}${busy ? " lifecycle-option-disabled" : ""}`}>
                        <input type="radio" name="version-lifecycle" checked={!releaseNow} onChange={() => setReleaseNowChoice(false)} disabled={busy} />
                        <span>
                          <span className="lifecycle-option-title">Save as draft</span>
                          <span className="lifecycle-option-desc">
                            Visible only to you — installers optional, everything editable, deletable with its records.
                          </span>
                        </span>
                      </label>
                      <label className={`lifecycle-option${releaseNow ? " lifecycle-option-selected" : ""}${busy ? " lifecycle-option-disabled" : ""}`}>
                        <input type="radio" name="version-lifecycle" checked={releaseNow} onChange={() => setReleaseNowChoice(true)} disabled={busy} />
                        <span>
                          <span className="lifecycle-option-title">Release now</span>
                          <span className="lifecycle-option-desc">
                            Becomes the SKU's fulfillment target immediately — permanent content, installers required for products.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                )}
                <p className="generate-codes-hint" style={{ textAlign: "left" }}>
                  {editing
                    ? "Installer file names must embed the version — a renumbered version needs fresh uploads."
                    : releaseNow
                      ? installersNeeded
                        ? "The new version becomes the latest release: its installer links replace the SKU's download links everywhere."
                        : "The new version becomes the latest release. This SKU only grants credits, so installers are optional."
                      : "Drafts don't need installers — add them any time before beta or release."}
                </p>
                {/* the version number lives on page 1 — this page is
                    lifecycle + installers only */}
                <InstallerFieldsView
                  fields={installer}
                  busy={busy}
                  installersRequired={installersNeeded}
                  hideVersion
                />
                {installersNeeded && (!macProvided || !winProvided) && (
                  <p className="generate-codes-hint">Provide an installer for both platforms — a file or a URL each.</p>
                )}
                {!installersNeeded && macProvided !== winProvided && (
                  <p className="generate-codes-hint">Installers come in pairs — provide a file or a URL for both platforms, or neither.</p>
                )}
              </div>
            )}

            {page === "version" && (
              <div className="generate-codes-form">
                {/* hotfix-only mode: the WIP occupies the feature lane, so
                    there is no intent to choose — state the fact instead */}
                {hotfixOnly && (
                  <p className="generate-codes-hint" style={{ textAlign: "left" }}>
                    v{wipVersion!.version} is still {wipVersion!.status} — this
                    ships a hotfix to the released line (suggests
                    v{suggestNextVersion(latestVersion!, "hotfix")}). The
                    plug-in set carries over unchanged from v{latestVersion}.
                  </p>
                )}
                {/* the versioning intent and the number it suggests belong
                    together: a hotfix stays below a feature draft (patch
                    bump), features leave the patch lane free (minor bump).
                    Suggestion only: the version field stays editable. */}
                {!editing && latestVersion !== null && !hotfixOnly && (
                  <div className="generate-codes-field">
                    <span className="generate-codes-label">What's in this version?</span>
                    <div className="lifecycle-options">
                      <label className={`lifecycle-option${intent === "hotfix" ? " lifecycle-option-selected" : ""}${busy ? " lifecycle-option-disabled" : ""}`}>
                        <input type="radio" name="version-intent" checked={intent === "hotfix"} onChange={() => chooseIntent("hotfix")} disabled={busy} />
                        <span>
                          <span className="lifecycle-option-title">Hotfix</span>
                          <span className="lifecycle-option-desc">
                            Bug fixes only — the plug-in set carries over unchanged and the
                            version releases directly, no draft stage. Suggests
                            v{suggestNextVersion(latestVersion, "hotfix")}, staying below a
                            feature draft.
                          </span>
                        </span>
                      </label>
                      <label className={`lifecycle-option${intent === "features" ? " lifecycle-option-selected" : ""}${busy ? " lifecycle-option-disabled" : ""}`}>
                        <input type="radio" name="version-intent" checked={intent === "features"} onChange={() => chooseIntent("features")} disabled={busy} />
                        <span>
                          <span className="lifecycle-option-title">Feature update</span>
                          <span className="lifecycle-option-desc">
                            New features — suggests v{suggestNextVersion(latestVersion, "features")}, leaving
                            the patch lane free for hotfixes while this version is in progress.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                )}
                <label className="generate-codes-field">
                  <span className="generate-codes-label">Version{requiredMark}</span>
                  <input
                    type="text"
                    value={installer.version}
                    onChange={(e) => installer.setVersion(e.target.value)}
                    className="generate-codes-input"
                    disabled={busy}
                    placeholder="e.g. 1.14.4"
                  />
                  {version !== "" && !versionParses ? (
                    <span className="generate-codes-error">Version must be MAJOR.MINOR.PATCH</span>
                  ) : versionError !== null ? (
                    <span className="generate-codes-error">{versionError}</span>
                  ) : versionWarning !== null ? (
                    <span style={{ color: "var(--nfa-yellow)", fontSize: "9pt" }}>{versionWarning}</span>
                  ) : version !== "" ? (
                    <span className="generate-codes-hint">Folder for the uploaded installer files: …/installers/{version}/</span>
                  ) : (
                    <span className="generate-codes-hint">MAJOR.MINOR.PATCH — e.g. 1.14.4</span>
                  )}
                </label>
                {editing && (
                  <p className="generate-codes-hint" style={{ textAlign: "left" }}>
                    Renumbering requires re-uploading the installers — their
                    file names embed the version.
                  </p>
                )}
              </div>
            )}

            {page === "products" && (
              <div className="generate-codes-form">
                <p className="generate-codes-hint" style={{ textAlign: "left" }}>
                  {editing
                    ? "The version's plug-in set — editable while it is unreleased; it freezes at release. Removing a plug-in revokes it from the version's current holders."
                    : <>The version's plug-in set — pre-filled from
                      {latestVersion !== null ? " the latest release" : " the newest version"} and
                      permanent once released. Removed products stay with their
                      current owners; whether new ones reach owners of older
                      versions is decided at release.</>}
                </p>
                {/* Two sections, each with its own header line: products
                    carried over from the previous version and products new to
                    this version. Rows classify live by their selected
                    product. Deliberately NO overflow wrapper anywhere here —
                    it would clip the ProductFilter typeahead dropdown (the
                    dialog content itself scrolls on narrow screens instead). */}
                {preExistingLicenseRows.length > 0 && (
                  <div className="generate-codes-field">
                    <span className="generate-codes-label" style={{ textAlign: "center", marginBottom: 8 }}>Pre-existing Products</span>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={gridHeaderStyle(licenseGridTemplate)}>
                        <span>Product</span>
                        <span title="Max activations">Seats</span>
                        <span title="Max activations per year (empty = unlimited)">Per year</span>
                        <span />
                      </div>
                      {preExistingLicenseRows.map(renderLicenseRow)}
                    </div>
                  </div>
                )}
                {/* extra air between the two sections (none needed on a
                    first release, when Pre-existing is hidden) */}
                <div className="generate-codes-field" style={{ marginTop: preExistingLicenseRows.length > 0 ? 16 : 0 }}>
                  <span className="generate-codes-label" style={{ textAlign: "center", marginBottom: 8 }}>New Products</span>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {newLicenseRows.length > 0 && (
                      <div style={gridHeaderStyle(licenseGridTemplate)}>
                        <span>Product</span>
                        <span title="Max activations">Seats</span>
                        <span title="Max activations per year (empty = unlimited)">Per year</span>
                        <span />
                      </div>
                    )}
                    {newLicenseRows.map(renderLicenseRow)}
                    {/* the add link rides the same grid template so it lines
                        up with the Product column's left edge; hidden while
                        an unfilled row (open search box) is waiting */}
                    {!licenseRows.some(r => r.product === null) && (
                      <div style={gridRowStyle(licenseGridTemplate)}>
                        <button
                          className="card-link"
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left", justifySelf: "start" }}
                          onClick={addLicenseRow}
                          disabled={busy}
                        >
                          + Add product
                        </button>
                      </div>
                    )}
                  </div>
                  {/* the product search is scoped to the sku owner's
                      products — an admin can deliberately widen it (e.g. an
                      NFA bundle carrying a partner's plug-in) */}
                  {/* one slot below the sections: while any row holds a
                      foreign product, the widen-search checkbox gives way to
                      a warning stating the fact (the search stays widened —
                      includeAllOwners is forced); with none, admins get the
                      checkbox back */}
                  {hasForeignProducts ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, alignSelf: "start", color: "var(--nfa-light-grey)", fontSize: "9pt" }}>
                      <span role="img" aria-label="warning">⚠️</span>
                      Version includes products you don't own — they're marked above.
                    </span>
                  ) : isAdmin && (
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, cursor: "pointer", alignSelf: "start" }}>
                      <input
                        type="checkbox"
                        className="retro-checkbox"
                        checked={includeAllOwners}
                        onChange={(e) => setAllOwnersProducts(e.target.checked)}
                        disabled={busy}
                      />
                      <span style={{ color: "var(--nfa-light-grey)", fontSize: "9pt" }}>
                        Allow products I don't own
                      </span>
                    </label>
                  )}
                  {seedSkipped > 0 && (
                    <span className="generate-codes-error">
                      {seedSkipped} existing plug-in{seedSkipped === 1 ? "" : "s"} could not be pre-filled — re-add {seedSkipped === 1 ? "it" : "them"} manually or cancel and retry.
                    </span>
                  )}
                </div>
              </div>
            )}

            {page === "summary" && (
              <div style={{ textAlign: "left", display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <span className="generate-codes-label">{editing ? "Changes" : releaseNow ? "Release" : "Draft"}</span>
                  <p style={{ margin: "4px 0 0" }}>
                    <strong>v{version}</strong>
                    <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
                      {macProvided ? " · installers for macOS and Windows" : " · no installers"}
                    </span>
                  </p>
                </div>

                {/* a hotfix carries the release's set verbatim (no claims
                    sent) — a diff against the seed would be noise */}
                {hotfixIntent ? (
                  <div>
                    <span className="generate-codes-label">Plug-ins</span>
                    <p style={{ margin: "4px 0 0", color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
                      Carried over unchanged from v{latestVersion} — a hotfix keeps the release's exact set.
                    </p>
                  </div>
                ) : (
                <div>
                  <span className="generate-codes-label">
                    Plug-in changes{editing ? ` vs the current v${editVersion.version}` : latestVersion !== null ? ` vs v${latestVersion}` : ""}
                  </span>
                  {licenseChanges.length === 0 ? (
                    <p style={{ margin: "4px 0 0", color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
                      No content changes — the same set as {editing ? "before" : latestVersion !== null ? `v${latestVersion}` : "before"}.
                    </p>
                  ) : (
                    <div style={{ margin: "6px 0 0", display: "flex", flexDirection: "column", gap: 4 }}>
                      {licenseChanges.map((change, i) => <ChangeLine key={`l${i}`} change={change} />)}
                    </div>
                  )}
                  {unchangedLicenseCount > 0 && hasChanges && (
                    <p style={{ margin: "6px 0 0", color: "var(--nfa-light-grey)", fontSize: "9pt" }}>
                      {unchangedLicenseCount} plug-in{unchangedLicenseCount === 1 ? "" : "s"} unchanged.
                    </p>
                  )}
                  {topupRows.length > 0 && (
                    <p style={{ margin: "6px 0 0", color: "var(--nfa-light-grey)", fontSize: "9pt" }}>
                      {topupRows.length} credits claim{topupRows.length === 1 ? "" : "s"} carried over unchanged.
                    </p>
                  )}
                </div>
                )}

                {/* the release moment's retroactivity decision — only when
                    releasing now, over an existing release, with new plug-ins
                    (drafts decide later, in the release dialog) */}
                {retroDecisionRows.length > 0 && (
                  <div>
                    <span className="generate-codes-label">
                      New plug-ins vs v{latestVersion} — grant to owners of older versions?
                    </span>
                    <div style={{ margin: "6px 0 0", display: "flex", flexDirection: "column", gap: 6 }}>
                      {retroDecisionRows.map((row) => (
                        <label key={row.key} style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            className="retro-checkbox"
                            checked={retroIds.has(row.product!.id)}
                            onChange={(e) => setRetroIds(prev => {
                              const next = new Set(prev)
                              if (e.target.checked) next.add(row.product!.id); else next.delete(row.product!.id)
                              return next
                            })}
                            disabled={busy}
                          />
                          <span>{row.product!.name}</span>
                        </label>
                      ))}
                    </div>
                    <p style={{ margin: "6px 0 0", color: "var(--nfa-light-grey)", fontSize: "9pt" }}>
                      Checked = every current owner of an older version receives this
                      plug-in's license at release (retroactive). Unchecked = new buyers only.
                    </p>
                  </div>
                )}

                {retroDepositNames.length > 0 && (
                  <div style={{ border: "1px solid var(--nfa-yellow)", borderRadius: 6, padding: "10px 12px" }}>
                    <p style={{ margin: 0, color: "var(--nfa-yellow)", fontSize: "10pt" }}>
                      Releasing immediately deposits {retroDepositNames.join(", ")} license{retroDepositNames.length === 1 ? "s" : ""} to
                      owners of every older version. Licenses are never taken
                      back by later releases — this cannot be undone.
                    </p>
                  </div>
                )}

                {editing && licenseChanges.length > 0 && editVersion.status !== SkuVersionStatus.Draft && (
                  <p style={{ margin: 0, color: "var(--nfa-yellow)", fontSize: "10pt" }}>
                    Saving re-syncs the {editVersion.status === SkuVersionStatus.Alpha
                      ? "team's alpha"
                      : "team's and beta testers'"} licenses: added
                    plug-ins are deposited, removed ones are revoked.
                  </p>
                )}

                <p style={{ margin: 0, color: "var(--nfa-light-grey)", fontSize: "9pt" }}>
                  {editing || !releaseNow
                    ? `The set above stays editable while v${version} is unreleased — it freezes at release.`
                    : `The set above becomes v${version}'s permanent content — it can only be revised by releasing another version.`}
                </p>

                {armed && (
                  <p style={{ margin: 0, color: "var(--nfa-yellow)", fontSize: "10pt" }}>
                    Are you sure? Click "Confirm release" to make it final
                    {retroDepositNames.length > 0 ? " and deposit the licenses" : ""}.
                  </p>
                )}
              </div>
            )}

            {/* the uploads run at submit time, while the SUMMARY page is
                showing — so the live progress renders here, next to the
                button being waited on (InstallerFieldsView shows the same
                line on its own page, but nobody is there during a save) */}
            {installer.uploadStatus && (
              <p style={{ color: "var(--nfa-orange)", fontSize: "10pt", margin: "12px 0 0" }}>
                {installer.uploadStatus.label} {installer.uploadStatus.percent}%
              </p>
            )}

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              {pageIndex > 0 && (
                <button onClick={() => goTo(pages[pageIndex - 1])} disabled={busy}>Back</button>
              )}
              {page !== "summary" ? (
                <button
                  className="btn-upgrade"
                  disabled={!pageValid[page] || busy}
                  onClick={() => goTo(pages[pageIndex + 1])}
                >
                  Next
                </button>
              ) : editing ? (
                <button
                  className="btn-upgrade"
                  disabled={!pageValid.summary || busy}
                  onClick={handleSubmit}
                >
                  {busy ? <><Spinner size={12} /> Saving…</> : "Save changes"}
                </button>
              ) : !releaseNow ? (
                <button
                  className="btn-upgrade"
                  disabled={!pageValid.summary || busy}
                  onClick={handleSubmit}
                >
                  {busy ? <><Spinner size={12} /> Saving…</> : `Save draft v${version}`}
                </button>
              ) : (
                <button
                  className={`btn-upgrade${armed && !busy ? " btn-armed" : ""}`}
                  disabled={!pageValid.summary || busy}
                  onClick={() => armed ? handleSubmit() : setArmed(true)}
                >
                  {busy ? <><Spinner size={12} /> Releasing…</>
                    : armed ? "Confirm release" : `Release v${version}`}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}

// Delete confirmation (DeleteOfferingDialog pattern, single tier).
// Released: only the LATEST released version (SkuPage only offers the button
// there), and only while nothing references it — the server refuses
// otherwise and the message surfaces below. Draft/beta: deletable any time,
// together with their records (self-grants, tester deposits).
export function DeleteVersionDialog({ skuId, version, accessToken, onClose, onDone }: {
  skuId: string
  version: SkuVersion
  accessToken: string
  onClose: () => void
  onDone: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"confirm" | "deleting" | "error">("confirm")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const handleDelete = async () => {
    setStep("deleting")
    setErrorMessage("")
    try {
      await Api.deleteSkuVersion({ skuId, versionId: version.id, accessToken })
      onDone()
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "deleting") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Delete Version</h2>

        {version.status === SkuVersionStatus.Released ? (
          <p>
            Delete version {version.version}? Its installer links and plug-in
            set will disappear, and the previous release becomes the latest.
            This only works while nothing references the version — once someone
            owns it (a purchase, grant, subscription or minted codes), it is
            permanent history. The uploaded installer files stay on the
            downloads bucket.
          </p>
        ) : (
          <p>
            Delete {skuVersionStatusLabels[version.status].toLowerCase()} version {version.version}?
            Its plug-in set is deleted with it, and every license it granted
            {version.status === SkuVersionStatus.Beta ? " — the team's and the beta testers' licenses —"
              : version.status === SkuVersionStatus.Alpha ? " — the team's alpha licenses —"
                : " (your dev license)"} is
            revoked and removed
            {(version.entitledUserCount ?? 0) > 0
              ? ` from ${version.entitledUserCount} user${version.entitledUserCount === 1 ? "" : "s"}`
              : ""}. The uploaded installer files stay on the downloads bucket.
          </p>
        )}

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={step === "deleting"}>Cancel</button>
          <button
            className="btn-upgrade"
            disabled={step === "deleting"}
            onClick={handleDelete}
          >
            {step === "deleting" ? <><Spinner size={12} /> Deleting…</> : "Delete"}
          </button>
        </div>
      </div>
    </dialog>
  )
}
